import { Tag, Typography } from 'antd';
import { RightOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { CSSProperties, ReactNode } from 'react';
import {
	mobileColor,
	mobileFontSize,
	mobileFontWeight,
	mobileRadius,
	mobileShadow,
	mobileSpacing,
	mobileType,
} from '../theme/mobile-typography.js';

export const MOBILE_ROOT_BG = mobileColor.page;
export const MOBILE_ROOT_PRIMARY = mobileColor.primary;

export interface SummaryItem {
	key: string;
	label: ReactNode;
	value: ReactNode;
	hint?: ReactNode;
	tone?: 'default' | 'success' | 'warning' | 'danger';
	active?: boolean;
	onClick?: () => void;
}

export interface ActionTileItem {
	key: string;
	title: ReactNode;
	subtitle?: ReactNode;
	icon: ReactNode;
	badge?: ReactNode;
	disabled?: boolean;
	onClick?: () => void;
}

export interface ActionRowItem {
	key: string;
	title: ReactNode;
	subtitle?: ReactNode;
	icon?: ReactNode;
	trailing?: ReactNode;
	onClick?: () => void;
}

export function MobileRootPage({ children, style }: { children: ReactNode; style?: CSSProperties }) {
	return (
		<div
			className="ulucu-mobile-root-page"
			style={{
				background: MOBILE_ROOT_BG,
				minHeight: '100vh',
				padding: `${mobileSpacing.xl}px ${mobileSpacing.lg}px calc(20px + env(safe-area-inset-bottom))`,
				...style,
			}}
		>
			<div style={{ maxWidth: 960, margin: '0 auto' }}>{children}</div>
		</div>
	);
}

export function NativeMirrorPage({ children, style }: { children: ReactNode; style?: CSSProperties }) {
	return (
		<div
			className="ulucu-native-mirror-page"
			style={{
				background: MOBILE_ROOT_BG,
				minHeight: '100vh',
				padding: '0 0 calc(16px + env(safe-area-inset-bottom))',
				...style,
			}}
		>
			<div style={{ maxWidth: 480, margin: '0 auto' }}>{children}</div>
		</div>
	);
}

export function NativeTopBar({
	title,
	onBack,
	action,
	backLabel,
}: {
	title: ReactNode;
	onBack?: () => void;
	action?: ReactNode;
	backLabel?: string;
}) {
	const { t } = useTranslation();
	const resolvedBackLabel = backLabel || t('common.back');
	return (
		<header
			style={{
				height: 48,
				background: mobileColor.surface,
				borderBottom: `1px solid ${mobileColor.divider}`,
				display: 'grid',
				gridTemplateColumns: '48px minmax(0, 1fr) 48px',
				alignItems: 'center',
				position: 'sticky',
				top: 0,
				zIndex: 10,
			}}
		>
			{onBack ? (
				<button
					type="button"
					aria-label={resolvedBackLabel}
					onClick={onBack}
					style={{
						border: 0,
						background: 'transparent',
						height: 48,
						color: mobileColor.text,
						fontSize: 18,
						cursor: 'pointer',
					}}
				>
					<span
						aria-hidden="true"
						style={{
							display: 'inline-block',
							width: 12,
							height: 12,
							borderLeft: '2px solid currentColor',
							borderBottom: '2px solid currentColor',
							transform: 'rotate(45deg)',
						}}
					/>
				</button>
			) : <span />}
			<div
				className="ulucu-native-topbar-title"
				style={{
					textAlign: 'center',
					color: mobileColor.text,
					fontSize: mobileFontSize.topBar,
					fontWeight: mobileFontWeight.medium,
					lineHeight: 1.2,
					overflow: 'hidden',
					textOverflow: 'ellipsis',
					whiteSpace: 'nowrap',
				}}
			>
				{title}
			</div>
			<div style={{ minWidth: 0 }}>{action}</div>
		</header>
	);
}

export function NativeSection({ title, children, style }: { title?: ReactNode; children: ReactNode; style?: CSSProperties }) {
	return (
		<section style={{ padding: `${mobileSpacing.xl}px ${mobileSpacing.lg}px 0`, ...style }}>
			{title && (
				<div className="ulucu-mobile-card-title" style={{ color: mobileColor.text, fontSize: mobileFontSize.title, fontWeight: mobileFontWeight.medium, margin: '0 2px 10px', lineHeight: 1.25 }}>
					{title}
				</div>
			)}
			{children}
		</section>
	);
}

export interface NativeGridItem {
	key: string;
	title: ReactNode;
	icon: ReactNode;
	muted?: boolean;
	onClick?: () => void;
}

export function NativeIconGrid({ items }: { items: NativeGridItem[] }) {
	return (
		<div
			className="ulucu-native-icon-grid"
			style={{
				background: '#fff',
				borderTop: `1px solid ${mobileColor.divider}`,
				borderBottom: `1px solid ${mobileColor.divider}`,
				display: 'grid',
				gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
			}}
		>
			{items.map((item) => (
				<button
					key={item.key}
					type="button"
					onClick={item.onClick}
					style={{
						minHeight: 82,
						border: 0,
						background: '#fff',
						padding: '12px 5px 10px',
						textAlign: 'center',
						cursor: 'pointer',
						color: item.muted ? mobileColor.textSubtle : mobileColor.text,
					}}
				>
					<div
						className="ulucu-mobile-icon"
						style={{
							width: 34,
							height: 34,
							margin: '0 auto 7px',
							borderRadius: mobileRadius.md,
							background: item.muted ? '#f0f0f0' : '#fff1eb',
							color: item.muted ? '#9aa0a6' : MOBILE_ROOT_PRIMARY,
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							fontSize: 19,
						}}
					>
						{item.icon}
					</div>
					<div
						style={{
							fontSize: 11,
							lineHeight: 1.18,
							minHeight: 27,
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							wordBreak: 'normal',
							overflowWrap: 'break-word',
						}}
					>
						{item.title}
					</div>
				</button>
			))}
		</div>
	);
}

export interface NativeListItem {
	key: string;
	title: ReactNode;
	icon?: ReactNode;
	trailing?: ReactNode;
	onClick?: () => void;
}

export function NativeList({ items }: { items: NativeListItem[] }) {
	return (
		<div style={{ background: mobileColor.surface, borderTop: `1px solid ${mobileColor.divider}`, borderBottom: `1px solid ${mobileColor.divider}` }}>
			{items.map((item, index) => {
				const content = (
					<>
						{item.icon && (
							<div className="ulucu-mobile-icon" style={{ width: 28, color: MOBILE_ROOT_PRIMARY, fontSize: 20, display: 'flex', justifyContent: 'center' }}>
								{item.icon}
							</div>
						)}
						<div className="ulucu-native-list-title" style={{ flex: 1, minWidth: 0, color: mobileColor.text, ...mobileType.title }}>
							{item.title}
						</div>
						{item.trailing ?? (item.onClick ? <RightOutlined style={{ color: mobileColor.textSubtle, fontSize: 12 }} /> : null)}
					</>
				);
				const style: CSSProperties = {
					minHeight: 52,
					padding: '0 14px',
					border: 0,
					borderTop: index === 0 ? 0 : `1px solid ${mobileColor.divider}`,
					background: mobileColor.surface,
					display: 'flex',
					alignItems: 'center',
					gap: 10,
					textAlign: 'start',
					width: '100%',
				};
				if (!item.onClick) return <div key={item.key} style={style}>{content}</div>;
				return (
					<button key={item.key} type="button" onClick={item.onClick} style={{ ...style, cursor: 'pointer' }}>
						{content}
					</button>
				);
			})}
		</div>
	);
}

export function NativeSimpleCard({ children, style }: { children: ReactNode; style?: CSSProperties }) {
	return (
		<div style={{ background: mobileColor.surface, borderTop: `1px solid ${mobileColor.divider}`, borderBottom: `1px solid ${mobileColor.divider}`, padding: 14, ...style }}>
			{children}
		</div>
	);
}

export function RootHeader({
	icon,
	title,
	subtitle,
	meta,
	action,
}: {
	icon: ReactNode;
	title: ReactNode;
	subtitle?: ReactNode;
	meta?: ReactNode;
	action?: ReactNode;
}) {
	return (
		<header style={{ padding: '2px 2px 14px' }}>
			<div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
				<div
					className="ulucu-mobile-icon"
					style={{
						width: 40,
						height: 40,
						borderRadius: mobileRadius.lg,
						background: '#1f2937',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						flex: '0 0 auto',
						color: '#fff',
						fontSize: 20,
					}}
				>
					{icon}
				</div>
				<div style={{ flex: 1, minWidth: 0 }}>
					<Typography.Title className="ulucu-mobile-header-title" level={4} style={{ margin: 0, fontSize: mobileFontSize.topBar, fontWeight: mobileFontWeight.semibold, lineHeight: 1.2 }}>
						{title}
					</Typography.Title>
					{subtitle && (
						<Typography.Text className="ulucu-mobile-meta-text" style={{ color: mobileColor.textMuted, fontSize: mobileFontSize.bodySmall }}>
							{subtitle}
						</Typography.Text>
					)}
				</div>
				{meta && <div style={{ flex: '0 0 auto' }}>{meta}</div>}
				{action && <div style={{ flex: '0 0 auto' }}>{action}</div>}
			</div>
		</header>
	);
}

export function RegionTag({ region }: { region: 'cn' | 'apac' }) {
	if (region === 'apac') return null;
	return (
		<Tag color="default" style={{ marginInlineEnd: 0 }}>
			CN
		</Tag>
	);
}

export function SummaryStrip({ items }: { items: SummaryItem[] }) {
	return (
		<div
			style={{
				display: 'grid',
				gridTemplateColumns: 'repeat(auto-fit, minmax(116px, 1fr))',
				gap: 8,
				marginBottom: 16,
			}}
		>
			{items.map((item) => {
				const content = (
					<>
					<div className="ulucu-mobile-meta-text" style={{ color: mobileColor.textMuted, fontSize: mobileFontSize.caption, lineHeight: 1.2 }}>{item.label}</div>
					<div className="ulucu-mobile-metric-text" style={{ color: toneColor(item.tone), fontSize: mobileFontSize.metric, fontWeight: mobileFontWeight.semibold, marginTop: 5, lineHeight: 1.1 }}>
						{item.value}
					</div>
					{item.hint && (
						<div className="ulucu-mobile-meta-text" style={{ color: mobileColor.textSubtle, fontSize: mobileFontSize.caption, marginTop: 4, lineHeight: 1.2 }}>
							{item.hint}
						</div>
					)}
					</>
				);
				const style: CSSProperties = {
					background: mobileColor.surface,
					border: item.active ? `1px solid ${toneColor(item.tone)}` : `1px solid ${mobileColor.border}`,
					borderRadius: mobileRadius.md,
					padding: '10px 10px 9px',
					minHeight: 72,
					boxShadow: item.active ? mobileShadow.active : mobileShadow.soft,
				};
				if (item.onClick) {
					return (
						<button
							key={item.key}
							type="button"
							onClick={item.onClick}
							style={{
								...style,
								textAlign: 'start',
								cursor: 'pointer',
							}}
						>
							{content}
						</button>
					);
				}
				return (
					<div key={item.key} style={style}>
						{content}
					</div>
				);
			})}
		</div>
	);
}

export function SectionBlock({
	title,
	extra,
	children,
	style,
}: {
	title?: ReactNode;
	extra?: ReactNode;
	children: ReactNode;
	style?: CSSProperties;
}) {
	return (
		<section style={{ marginBottom: 16, ...style }}>
			{(title || extra) && (
				<div style={{ display: 'flex', alignItems: 'center', margin: '0 4px 10px', gap: 8 }}>
					{title && (
						<Typography.Title className="ulucu-mobile-card-title" level={5} style={{ margin: 0, fontSize: mobileFontSize.title, fontWeight: mobileFontWeight.medium, flex: 1 }}>
							{title}
						</Typography.Title>
					)}
					{extra}
				</div>
			)}
			{children}
		</section>
	);
}

export function ActionTileGrid({ items, min = 132 }: { items: ActionTileItem[]; min?: number }) {
	return (
		<div
			style={{
				display: 'grid',
				gridTemplateColumns: `repeat(auto-fill, minmax(min(${min}px, 100%), 1fr))`,
				gap: 9,
			}}
		>
			{items.map((item) => (
				<button
					key={item.key}
					type="button"
					disabled={item.disabled}
					onClick={item.onClick}
					style={{
						position: 'relative',
						border: `1px solid ${mobileColor.border}`,
						background: mobileColor.surface,
						borderRadius: mobileRadius.md,
						padding: '11px 10px',
						minHeight: 106,
						textAlign: 'start',
						cursor: item.disabled ? 'not-allowed' : 'pointer',
						boxShadow: mobileShadow.soft,
						opacity: item.disabled ? 0.56 : 1,
					}}
				>
					<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
						<div
							className="ulucu-mobile-icon"
							style={{
								width: 40,
								height: 40,
								borderRadius: mobileRadius.lg,
								background: mobileColor.primarySoft,
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								color: MOBILE_ROOT_PRIMARY,
								fontSize: 22,
								flex: '0 0 auto',
							}}
						>
							{item.icon}
						</div>
						{item.badge}
					</div>
					<div className="ulucu-mobile-card-title" style={{ marginTop: 11, color: mobileColor.textStrong, fontSize: mobileFontSize.body, fontWeight: mobileFontWeight.medium, lineHeight: 1.2 }}>
						{item.title}
					</div>
					{item.subtitle && (
						<div className="ulucu-mobile-meta-text" style={{ marginTop: 6, color: mobileColor.textMuted, fontSize: mobileFontSize.meta, lineHeight: 1.35 }}>
							{item.subtitle}
						</div>
					)}
				</button>
			))}
		</div>
	);
}

export function ActionList({ items }: { items: ActionRowItem[] }) {
	return (
		<div style={{ background: mobileColor.surface, border: `1px solid ${mobileColor.border}`, borderRadius: mobileRadius.md, overflow: 'hidden', boxShadow: mobileShadow.soft }}>
			{items.map((item, index) => {
				const content = (
					<>
						{item.icon && (
							<div className="ulucu-mobile-icon" style={{ color: MOBILE_ROOT_PRIMARY, fontSize: 20, width: 24, display: 'flex', justifyContent: 'center' }}>
								{item.icon}
							</div>
						)}
						<div style={{ flex: 1, minWidth: 0 }}>
							<div className="ulucu-native-list-title" style={{ color: mobileColor.textStrong, fontSize: mobileFontSize.body, fontWeight: mobileFontWeight.medium, lineHeight: 1.25 }}>{item.title}</div>
							{item.subtitle && (
								<div className="ulucu-mobile-meta-text" style={{ color: mobileColor.textMuted, fontSize: mobileFontSize.meta, marginTop: 3, lineHeight: 1.3 }}>{item.subtitle}</div>
							)}
						</div>
						{item.trailing ?? (item.onClick ? <RightOutlined style={{ color: mobileColor.textSubtle, fontSize: 12 }} /> : null)}
					 </>
				);
				const baseStyle: CSSProperties = {
					width: '100%',
					minHeight: 56,
					border: 0,
					borderTop: index === 0 ? 0 : `1px solid ${mobileColor.divider}`,
					background: mobileColor.surface,
					display: 'flex',
					alignItems: 'center',
					gap: 12,
					padding: '12px 14px',
					textAlign: 'start',
				};
				if (!item.onClick) {
					return (
						<div key={item.key} style={{ ...baseStyle, cursor: 'default' }}>
							{content}
						</div>
					);
				}
				return (
					<button
						key={item.key}
						type="button"
						onClick={item.onClick}
						style={{ ...baseStyle, cursor: 'pointer' }}
					>
						{content}
					</button>
				);
			})}
		</div>
	);
}

function toneColor(tone?: SummaryItem['tone']): string {
	if (tone === 'success') return '#047857';
	if (tone === 'warning') return '#b45309';
	if (tone === 'danger') return '#b42318';
	return '#111827';
}
